import * as React from "react"
import {
    DataGrid,
} from "@mui/x-data-grid"
import { AXIOS_API_INSTANCE } from "../Constants"
import { send_authorized_request } from "../Constants/cognito"
import SearchIcon from '@mui/icons-material/Search';

import { Box, debounce, InputAdornment, TextField } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { SimpleCustomToolbar } from "./CustomToolbar"
import LocationPage from "./LocationPage"
import DocumentTitle from "../Utils/DocumentTitle"
import { formatPhoneNumber, widthChangeEvent } from "../Utils"

const columns = () => {
    return [
        {
            field: "name",
            headerName: "name",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
        },
        {
            field: "email",
            headerName: "Email",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
        },
        {
            field: "phone",
            headerName: "Phone",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
            valueFormatter: (value) => formatPhoneNumber(value)
        },
        {
            field: "address",
            headerName: "Address",
            minWidth: 150,
            flex: 1,
            editable: false,
            sortable: false,
            filterable: false,
            valueGetter: (value, row) =>
                row.address_1 + " " + row.address_2,
        },
        {
            field: "city",
            headerName: "City",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
        },
        {
            field: "state",
            headerName: "State",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
        },
        {
            field: "zip",
            headerName: "Zip Code",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
        },
        {
            field: "tax_rate",
            headerName: "Tax Rate",
            minWidth: 150,
            flex: 1,
            editable: false,
            filterable: false,
            valueFormatter: (value) => {
                return `${value ?? ""}%`
            },
        },
    ]
}

function Locations() {
    DocumentTitle('Locations')
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`locations:pageSize`) || 10),
        page: 0,
    });
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0 })
    const [sorting, setSorting] = React.useState({ field: 'name', sort: 'asc' })
    const [isLoading, setIsLoading] = React.useState(true)
    const [locationPage, setLocationPage] = React.useState({
        open: false,
        location: {},
    })
    const [locationSearch, setLocationSearch] = React.useState('')
    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)
    console.log("RENDERING Locations")

    const locationRowClick = (params) => {
        setLocationPage({
            location: params.row,
            open: true,
        })
    }
    const closeLocationPage = (changes) => {
        setLocationPage({ ...locationPage, open: false })
        if (changes) {
            getLocations(paginationModel, sorting.field, sorting.sort, locationSearch)
        }
    }
    const updateLocationSearch = (event) => {
        setLocationSearch(event.target.value.trim())
        if (event.target.value.trim() !== locationSearch) {
            debouncedGetLocations(paginationModel, sorting.field, sorting.sort, event.target.value.trim())
        }
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`locations:pageSize`, model.pageSize)
        setPaginationModel(model)
    }

    const getLocations = (paginationModel, sortField, sortDirection, locationSearch) => {
        setIsLoading(true)
        let my_filters = {}
        if (locationSearch.length > 0) {
            my_filters['or'] = [
                { 'name.ilike': locationSearch },
                { 'address_1.ilike': locationSearch },
                { 'address_2.ilike': locationSearch },
                { 'email.ilike': locationSearch },
                { 'city.ilike': locationSearch },
                { 'phone.ilike': locationSearch }
            ]
        }
        send_authorized_request(() => AXIOS_API_INSTANCE.get("locations", {
            params: {
                page_num: paginationModel.page,
                page_size: paginationModel.pageSize,
                order_by: sortField,
                order_by_direction: sortDirection,
                filters: JSON.stringify(my_filters),
            },
        }).then((location) => {
            for (const loc of location.data.data) {
                loc.user_ids = loc.users.map((user) => user.id)
            }
            setRowData({
                rows: location.data.data,
                total_count: location.data.total_count,
            })
            setIsLoading(false)
        })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetLocations = React.useMemo(() => debounce((paginationModel, sortField, sortDirection, locationSearch) => getLocations(paginationModel, sortField, sortDirection, locationSearch), 400), []);


    React.useEffect(() => {
        getLocations(paginationModel, sorting.field, sorting.sort, locationSearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, sorting])

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    return (
        (<React.Fragment>
            <Grid container space={1} sx={{ width: '100%' }}>
                <Grid size="grow">
                    {!locationPage.open &&
                        <Box>
                            <Grid container sx={{ padding: "0" }} justifyContent={"space-between"} alignItems={"center"}>
                                <Grid>
                                    <TextField
                                        autoFocus
                                        margin="dense"
                                        id="locationSearch"
                                        label="Search Locations"
                                        type="text"
                                        variant="filled"
                                        disabled={isLoading}
                                        value={locationSearch}
                                        onChange={updateLocationSearch}
                                        slotProps={{
                                            input: {
                                                inputProps: { min: 1 },
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        {<SearchIcon />}
                                                    </InputAdornment>
                                                ),
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid>
                                </Grid>
                            </Grid>
                            <DataGrid
                                key={windowInnerWidth}
                                slots={{
                                    toolbar: () => SimpleCustomToolbar(),
                                }}
                                rows={rowData.rows}
                                rowCount={rowData.total_count}
                                columns={columns()}
                                disableRowSelectionOnClick
                                experimentalFeatures={{ newEditingApi: true }}
                                autoHeight={true}
                                filterMode={"server"}
                                loading={isLoading}
                                pageSizeOptions={[5, 10, 25, 50, 100]}
                                pagination
                                paginationMode="server"
                                paginationModel={paginationModel}
                                onPaginationModelChange={setMyPaginationModel}
                                onRowClick={locationRowClick}
                                onSortModelChange={(params) => {
                                    if (params[0] !== undefined) {
                                        setSorting(params[0])
                                    } else {
                                        setSorting({ field: 'name', sort: 'asc' })
                                    }
                                }}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            city: false,
                                            state: false,
                                            zip: false,
                                        },
                                    },
                                }}
                            />
                        </Box>
                    }
                    {locationPage.open &&
                        <LocationPage
                            location={locationPage.location}
                            closeFunction={closeLocationPage}
                        />
                    }
                </Grid>
            </Grid>
        </React.Fragment>)
    );
}

export default Locations
